import { useQuery } from "@tanstack/react-query";
import { Card, Drawer, Tag, Typography } from "antd";
import type { TableProps } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import { compareNumeric, compareText } from "../api/format";
import type {
  CongregationSpeakerStatisticRow,
  ListResponse,
  SpeakerCongregationSpeechRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";

/**
 * Sidebar entry "congregation_speaker_statistics" → /congregation/speaker_statistics.
 *
 * The local coordinator's counterpart to the speaker's congregation
 * statistics: who has spoken here and how often, drilling down into the talks
 * they held. Same shape as /speaker/congregation_statistics — only the side of
 * the relation differs, and with it which of the five filters narrows the list
 * itself and which one narrows the assignments behind it.
 */
export function CongregationSpeakerStatisticsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterValues>({});
  const [selected, setSelected] = useState<CongregationSpeakerStatisticRow | null>(null);

  const speakers = useQuery({
    queryKey: ["congregation", "speaker_statistics", filters],
    queryFn: () =>
      apiFetch<ListResponse<CongregationSpeakerStatisticRow>>(
        withQuery("/congregation/speaker_statistics", filters),
      ),
  });

  const speeches = useQuery({
    queryKey: ["congregation", "speaker_statistics", "speeches", selected?.id, filters],
    queryFn: () =>
      apiFetch<ListResponse<SpeakerCongregationSpeechRow>>(
        withQuery(`/congregation/speaker_statistics/${selected!.id}/speeches`, {
          speech_filter: filters.speech_filter ?? "",
          language_filter: filters.language_filter ?? "",
        }),
      ),
    enabled: selected !== null,
  });

  const fields = useMemo(
    () => [
      { name: "speaker_filter", label: t("activerecord.models.speaker") },
      { name: "congregation_filter", label: t("activerecord.models.congregation") },
      { name: "circumscription_filter", label: t("activerecord.models.circumscription") },
      { name: "speech_filter", label: t("activerecord.models.speech") },
      { name: "language_filter", label: t("activerecord.models.language") },
    ],
    [t],
  );

  const columns = useMemo<TableProps<CongregationSpeakerStatisticRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.congregation.assignations"),
        dataIndex: "assignation_count",
        width: 110,
        align: "right",
        defaultSortOrder: "descend",
        sorter: (a, b) => a.assignation_count - b.assignation_count,
        render: (value: number) => (value > 0 ? <Tag color="blue">{value}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.speaker.first_name"),
        dataIndex: "first_name",
        width: 150,
        sorter: (a, b) => compareText(a.first_name, b.first_name),
      },
      {
        title: t("activerecord.attributes.speaker.last_name"),
        dataIndex: "last_name",
        width: 150,
        sorter: (a, b) => compareText(a.last_name, b.last_name),
      },
      {
        title: t("activerecord.attributes.circumscription.short"),
        dataIndex: "circumscription_code",
        width: 90,
        sorter: (a, b) => compareText(a.circumscription_code, b.circumscription_code),
      },
      {
        title: t("activerecord.attributes.congregation.congregation_code"),
        dataIndex: "congregation_code",
        width: 90,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "congregation_denomination",
        ellipsis: true,
        sorter: (a, b) => compareText(a.congregation_denomination, b.congregation_denomination),
      },
      { title: t("activerecord.attributes.user.phone"), dataIndex: "phone", width: 140 },
      { title: t("activerecord.attributes.user.mobile"), dataIndex: "mobile", width: 140 },
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "email",
        width: 200,
        ellipsis: true,
      },
      { title: t("activerecord.attributes.user.zip_code"), dataIndex: "zip_code", width: 90 },
      { title: t("activerecord.attributes.user.country"), dataIndex: "country", width: 90 },
    ],
    [t],
  );

  const speechColumns = useMemo<TableProps<SpeakerCongregationSpeechRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 170,
        defaultSortOrder: "descend",
        // The label is built for reading; the plain date is what orders it.
        sorter: (a, b) => compareText(a.week_begin, b.week_begin),
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "schema",
        width: 100,
        sorter: (a, b) => compareNumeric(a.schema, b.schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 100,
      },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.congregation_speaker_statistics")}>
      <FilterBar fields={fields} onApply={setFilters} loading={speakers.isFetching} />

      <DataTable
        columns={columns}
        rows={speakers.data?.data}
        loading={speakers.isPending}
        error={speakers.error}
        exportAs={t("navigation.congregation_speaker_statistics")}
        onRowClick={setSelected}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        size={960}
        destroyOnHidden
        title={
          selected && (
            <Typography.Text>
              {[selected.first_name, selected.last_name].filter(Boolean).join(" ")}
            </Typography.Text>
          )
        }
      >
        <Typography.Title level={5}>{t("speaker.speeches")}</Typography.Title>

        {/* Named after the speaker: the file has to say whose visits it is. */}
        <DataTable
          columns={speechColumns}
          rows={speeches.data?.data}
          exportAs={[selected?.first_name, selected?.last_name, t("speaker.speeches")]
            .filter(Boolean)
            .join(" · ")}
          loading={speeches.isPending}
          error={speeches.error}
          paginated={false}
        />
      </Drawer>
    </Card>
  );
}
